/**
 * @fileoverview A datasource for ExternalAccounts that uses AJAX to fetch / send data
 * asynchronously from controller endpoints
 *
 * @author Ahsan Qureshi (aqureshi@yext.com)
 */

goog.declareModuleId('yext.externalaccounts.AjaxSource');

import Uri from 'goog:goog.Uri';
import ErrorCode from 'goog:goog.net.ErrorCode';
import XhrIo from 'goog:goog.net.XhrIo';

import {ExternalAccounts} from '/js/yext/externalaccounts/routes';

import {Publisher} from '/js/yext/externalaccounts/models/publisher';

export default class AjaxSource {
  constructor() {
    /** @type {!Map<string, !XhrIo>} */
    this.currentRequests = new Map();
  }

  /**
   * @param {number} publisherId
   * @param {AbortSignal} abortSignal
   * @return {!Promise}
   */
  fetchExternalAccounts(publisherId, abortSignal) {
    const key = 'FETCH_EXTERNAL_ACCOUNTS';

    const query = new Uri.QueryData();
    query.set('publisherId', publisherId);
    return this.doRequest(
      key,
      abortSignal,
      new Uri(ExternalAccounts.fetchExternalAccounts())
        .setQueryData(query),
      'GET');
  }

  /**
   * @param {number} publisherId
   * @param {AbortSignal} abortSignal
   * @return {!Promise<Publisher>}
   */
  fetchPublisherInfo(publisherId, abortSignal) {
    const key = 'FETCH_PUBLISHER_INFO';

    const query = new Uri.QueryData();
    query.set('publisherId', publisherId);
    return this.doRequest(key,
      abortSignal,
      new Uri(ExternalAccounts.fetchPublisherInfo()).setQueryData(query),
      'GET');
  }

  saveExternalAccount(saveExternalAccountRequest, code, abortSignal) {
    const key = 'SAVE_EXTERNAL_ACCOUNT';

    const query = new Uri.QueryData();
    query.set('saveExternalAccountRequest',
      JSON.stringify(saveExternalAccountRequest));
    query.set('code', code);
    return this.doRequest(
      key,
      abortSignal,
      new Uri(ExternalAccounts.saveExternalAccount()),
      'POST',
      query.toString());
  }

  saveYextOrgAccount(authPartnerId, abortSignal) {
    const key = 'SAVE_YEXT_ORG_ACCOUNT';

    const query = new Uri.QueryData();
    query.set('authPartnerId', authPartnerId);
    return this.doRequest(
      key,
      abortSignal,
      new Uri(ExternalAccounts.saveYextOrgAccount()),
      'POST',
      query.toString());
  }

  /**
   * @private
   * @param {string} key
   * @param {AbortSignal} abortSignal
   * @param {!Uri} uri
   * @param {string} method
   * @param {string=} body
   * @return {!Promise}
   */
  doRequest(key, abortSignal, uri, method, body) {
    if (abortSignal.aborted) {
      return Promise.reject(true);
    }
    // Check to see if there is existing outbound request with the same key; if so, abort the
    // existing XMLHttpRequest and send a new one.
    if (this.currentRequests.has(key)) {
      this.currentRequests.get(key).abort();
      this.currentRequests.delete(key);
    }

    return new Promise((resolve, reject) => {
      const xhr = XhrIo.send(uri, e => {
        this.currentRequests.delete(key);
        if (e.target.isSuccess()) {
          resolve(e.target.getResponseJson());
        } else {
          reject(this.isAbort(e.target.getLastErrorCode()));
        }
      },
      method,
      body,
      {
        'X-Requested-With': 'XMLHttpRequest',
      },
      );
      this.currentRequests.set(key, xhr);
      abortSignal.onabort = () => {
        xhr.abort();
        if (this.currentRequests.has(key)) {
          this.currentRequests.delete(key);
        }
      };
    });
  }

  /**
   * @private
   * @param {!ErrorCode} error
   * @return {boolean} true if the request was aborted
   */
  isAbort(error) {
    return error === ErrorCode.ABORT;
  }
}
